import React, { useRef, useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { CSVLink } from "react-csv";
import { Button, Tabs, Tab, Box, Checkbox } from '@mui/material';
import { ProductApi } from "../api/productApi";
import { setSuccessNotification } from '../actions/notificationAction'
import { getSerialNumber } from '../utils/sharedFunctions'
import { productSorting } from "../config/sortingLabels";
import { productCSVHeaders } from "../config/csvHeaders";
import useListingActions from '../hooks/useListingActions'
import { actionBtn } from '../styles'
import { Product, ProductListingPage, ListTableActions, BulkLowStockDialog } from '../shared-components';
import useTranslation from '../hooks/useTranslation'

const ProductContainer = () => {
  const [dataForExport, setDataForExport] = useState([])
  const [tab, setTab] = useState('visible')
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkState, setBulkState] = useState({ open: false, saving: false, enabled: true, threshold: 10, });
  const csvButtonRef = useRef(null)
  const dispatch = useDispatch()
  const history = useHistory()
  const { t } = useTranslation()

  const getExtraParmsList = () => ({ filter: tab })

  const {
    data,
    count,
    page,
    perPage,
    searchTerm,
    setData,
    sortOptionSelected,
    handlerForSearchingAndSortingChange,
    fetchData,
  } = useListingActions('products', ProductApi.getProducts, 'latest', getExtraParmsList );

  const openBulkDialog = () => {
    setBulkState((prev) => ({ ...prev, open: true }));
  };
  const closeBulkDialog = () => {
    setBulkState((prev) => ({ ...prev, open: false }));
  };
  const handleBulkEnabledChange = (enabled) => {
    setBulkState((prev) => ({ ...prev, enabled }));
  };
  
  const handleBulkThresholdChange = (value) => {
    setBulkState((prev) => ({ ...prev, threshold: value }));
  };

  const toggleSelect = (productId, checked) => {
    setSelectedIds((prev) =>
      checked ? [...new Set([...prev, productId])] : prev.filter((id) => id !== productId)
    );
  };

  const handleToggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(data.map((p) => p.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleBulkLowStockSave = async () => {
    if (!selectedIds.length) return;
    try {
      setBulkState((prev) => ({ ...prev, saving: true }));
      await ProductApi.bulkUpdateLowStock({
        product_ids: selectedIds,
        low_stock_enabled: bulkState.enabled,
        low_stock_threshold: Number(bulkState.threshold) || 0,
      });
      dispatch(setSuccessNotification({ message: t('response_messages.products.bulk_low_stock_updated') }));
      setBulkState((prev) => ({ ...prev, open: false, saving: false }));
      fetchData({ page, per_page: perPage, q: searchTerm, sort_by: sortOptionSelected });
    } catch (e) {
      setBulkState((prev) => ({ ...prev, saving: false }));
    }
  };

  let getProducts = async (query='',sort_by='Name ascending', exportRows=false ) => {
    const response = await ProductApi.getProducts({
      page: page, per_page: perPage, q: query, sort_by: sort_by, export_records: exportRows, filter: tab
    })
    return response.data.data
  }

  const newProduct = () => history.push('/admin/products/new')

  const ACTION_BUILDERS = {
    delete: (product) => ({
      request: () => ProductApi.removeProduct(product),
      expectedStatus: 204,
      successMessage: t('response_messages.products.deleted'),
      requiresConfirm: true,
    }),
    archive: (product) => ({
      request: () => ProductApi.archiveProduct(product.id),
      expectedStatus: 200,
      successMessage: t('response_messages.products.archived'),
      requiresConfirm: true,
    }),
    unarchive: (product) => ({
      request: () => ProductApi.unarchiveProduct(product.id),
      expectedStatus: 200,
      successMessage: t('response_messages.products.restored'),
      requiresConfirm: true,
    }),
  };
  
  const handleProductAction = async (actionKey, product) => {
    const actionConfig = ACTION_BUILDERS[actionKey]?.(product);
    if (!actionConfig) return;
  
    if (actionConfig.requiresConfirm && !window.confirm(global.constants.labels.are_you_sure)) return;
  
    const response = await actionConfig.request();
    if (response?.status === actionConfig.expectedStatus) {
      setData((prev) => prev.filter((p) => p.id !== product.id));
      dispatch(setSuccessNotification({ message: actionConfig.successMessage }));
    }
  };  

  const exportCSV = async () => {
    const dataToExport = await getProducts(searchTerm, sortOptionSelected, true )
    dataToExport?.map((product)=>{
      product.variants = product.variants.map(variant => variant.name).join(', ')
      product.category = product.category_slug
      product.createdAt = new Date(product.createdAt).toLocaleString('en-US')
    })
    if (dataToExport.length > 0) {
      setDataForExport(dataToExport)
      csvButtonRef.current.link.click()
    }
  }

  useEffect(() => {
    setSelectedIds([]);
    fetchData({ page: 1, per_page: perPage, q: searchTerm, sort_by: sortOptionSelected })
  }, [tab])

  const tableActions = (
    <>
      <Box sx={{ mb: 1 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="product-visibility-tabs">
          <Tab value="visible"  label="Public" />
          <Tab value="hidden"   label="Hidden" />
          <Tab value="archived" label="Archived" />
        </Tabs>
      </Box>

      <ListTableActions
        searchTerm={searchTerm}
        searchText={t('labels.search_products')}
        sortOptionSelected={sortOptionSelected}
        getData={handlerForSearchingAndSortingChange}
        translateText={t}
        sortOptions={productSorting}
      />
    </>
  )

  const handleReorder = () => { history.push(`/admin/products/reorder`);};

  const buttons = (
    <>
      <CSVLink
        data={dataForExport}
        headers={productCSVHeaders}
        filename={`products-${(new Date()).toLocaleDateString().replace(', ', '-')}.csv`}
        className='hidden'
        ref={csvButtonRef}
        target='_blank'/>
      <Button variant="outlined" sx={actionBtn} onClick={handleReorder}>{t('labels.reorder_products')}</Button>  
      <Button variant="outlined" sx={actionBtn} disabled={selectedIds.length === 0} onClick={() => openBulkDialog(true)} > Bulk low stock </Button>
      <Button variant="outlined" sx={actionBtn}
              onClick={exportCSV}>{t('labels.exportToCsv')}</Button>
      <Button variant="contained" sx={actionBtn} onClick={newProduct}>{t('labels.create_product')}</Button>
    </>
  )

  const tableContent = productTableContent(handleProductAction, data, page, perPage, tab === 'archived', selectedIds, toggleSelect, handleToggleSelectAll)

  return (
    <>
      <ProductListingPage
        title={t('labels.menu.products')}
        actions={buttons}
        tableContent={tableContent}
        count={count}
        page={page}
        perPage={perPage}
        tableActions={tableActions}
        handlerForSearchingAndSortingChange={handlerForSearchingAndSortingChange}
      />
      <BulkLowStockDialog
        open={bulkState.open}
        saving={bulkState.saving}
        enabled={bulkState.enabled}
        threshold={bulkState.threshold}
        selectedCount={selectedIds.length}
        onClose={closeBulkDialog}
        onChangeEnabled={handleBulkEnabledChange}
        onChangeThreshold={handleBulkThresholdChange}
        onSubmit={handleBulkLowStockSave}
      />
    </>
  )
}

const productTableContent = ( onAction, products = [], page_no, per_page, isArchivedTab, selectedIds = [], onToggleSelect, onToggleSelectAll ) => {
  const history = useHistory();
  const { t } = useTranslation();
  const handleEdit = (product) => {
    history.push({
      pathname: `/admin/products/edit/${product.id}`,
      state: { productName: product.name },
    });
  };

  const allSelected = products.length > 0 && selectedIds.length === products.length;
  const partiallySelected = selectedIds.length > 0 && !allSelected;

  const productBody = products.map((product, index) => (
    <Product key={product.id} product={product} onAction={(a) => onAction(a, product)} isArchivedTab={isArchivedTab} index={getSerialNumber(page_no, per_page, index)} handleEdit={handleEdit} selected={selectedIds.includes(product.id)} onToggleSelect={onToggleSelect} />
  ))

  return {
    productBody,
    productHeading: [
      <Checkbox
        indeterminate={partiallySelected}
        checked={allSelected}
        onChange={(e) => onToggleSelectAll(e.target.checked)}
        onClick={(e) => e.stopPropagation()}
      />,
      '#','',t('labels.name'),t('labels.article_no'),t('labels.category'),t('labels.public'),t('labels.action'),
    ],
  }
}

export default ProductContainer